import { Box, Grow, Stack, Typography } from "@mui/material";
import { BlDataTable } from "Shared/DataTable/BlDataTable";
import styled from "@emotion/styled";
import { FunctionComponent } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import { formatDate } from "Infrastructure/Utils/DateUtils";
import { getProductTypesDisplayName } from "Infrastructure/Utils/Nsure/ProductUtils";
import {
	getInfoWarningPlacementsDisplayName,
	getInfoWarningTypeDisplayName,
	getInfoWarningVisibilityDisplayName,
} from "Infrastructure/Utils/Nsure/InfoWarningUtils";
import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { useSnackbar } from "Infrastructure/Providers/SnackbarProvider/SnackbarProvider";
import { InfoWarningDeleteByIdMessage } from "Infrastructure/Models/Messages/Messages";
import { logError } from "Infrastructure/Utils/LoggingUtils";
import { CloseDialogHandler } from "Infrastructure/Providers/DialogProvider/DialogProvider";
import { NoTasks } from "Shared/Icons/NoTasks";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { BlConfirmButton } from "Shared/Buttons/BlConfirmButton";
import {
	InfoWarning,
	InfoWarningState,
} from "Infrastructure/Models/Messages/Dto/InfoWarning";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { BlTooltip } from "Shared/BlTooltip";

const StyledCell = styled(Typography)``;

type Props = {
	deviceName: string | null;
	infoWarnings: InfoWarning[] | undefined;
	hubConnection: HubConnection | null;
	closeDialog: CloseDialogHandler;
};

export const InfoWarningsTable: FunctionComponent<Props> = (props) => {
	const { deviceName, infoWarnings, hubConnection, closeDialog } = props;

	const showSnackbar = useSnackbar();
	const { t } = useResource();

	if (!infoWarnings || infoWarnings?.length === 0) {
		return (
			<Grow in={true}>
				<Stack justifyContent="center" alignItems="center">
					<NoTasks />
					<Typography variant="h2" color="primary" marginBottom={4}>
						Žádné infolišty
					</Typography>
				</Stack>
			</Grow>
		);
	}

	const deleteInfoWarning = (infoWarningId: number | undefined) => {
		if (!infoWarningId || !deviceName) {
			return;
		}
		if (
			!hubConnection ||
			hubConnection.state !== HubConnectionState.Connected
		) {
			showSnackbar(t(Resources.Errors.Hub.ConnectionError), "error");
			return;
		}
		const infoWarningDeleteByIdMessage: InfoWarningDeleteByIdMessage = {
			filteredDeviceNames: [deviceName],
			infoWarningID: infoWarningId,
			notifyClientByEmail: false,
		};
		hubConnection
			.invoke("infoWarningDeleteById", infoWarningDeleteByIdMessage)
			.catch((error: Error) => {
				showSnackbar(t(Resources.Errors.Hub.RequestError), "error");
				logError(error);
			});

		closeDialog();
	};

	const isJiraTicket = (description: string | null | undefined): boolean => {
		if (!description) return false;
		return /^N[A-Z]+-\d+$/.test(description);
	};

	const renderDescription = (
		description: string | null | undefined,
		textColor: string
	) => {
		if (isJiraTicket(description)) {
			return (
				<a
					href={`https://blogic.atlassian.net/browse/${description}`}
					target="_blank"
					rel="noopener noreferrer"
					style={{ color: textColor, textDecoration: "underline" }}
				>
					{description}
				</a>
			);
		}
		return description;
	};

	return (
		<>
			<BlDataTable
				rows={infoWarnings.map((x) => ({ data: x }))}
				rowEntityID="infoWarningID"
				getHeaderCell={(column) => (
					<Typography>
						<Box component="span" fontWeight="fontWeightMedium">
							{column.label ?? column.id}
						</Box>
					</Typography>
				)}
				columns={[
					{ id: "text", label: "Text" },
					{ id: "validFrom", label: "Platnost od" },
					{ id: "validTo", label: "Platnost do" },
					{ id: "url", label: "URL link" },
					{ id: "type", label: "Typ upozornění" },
					{ id: "placement", label: "Umístění" },
					{ id: "productTypeID", label: "Produkt" },
					{ id: "visibility", label: "Viditelnost" },
					{ id: "description", label: "Identifikátor\u00A0/\u00A0popis" },
					{ id: "action", label: "", maxWidth: 0, justifySelf: "flex-end" },
				]}
				rowsPerPage={10}
				getCell={(id, data) => {
					switch (id) {
						case "text":
							return (
								<StyledCell
									color={
										data.state === InfoWarningState.Expired
											? "#e1e1e1"
											: "textPrimary"
									}
								>
									<Stack direction="row" alignItems="center">
										<Stack mr={1}>
											{data.state === InfoWarningState.Expired && (
												<BlTooltip title="Expirovaná">
													<HourglassBottomIcon sx={{ cursor: "help" }} />
												</BlTooltip>
											)}
											{data.state === InfoWarningState.Pending && (
												<BlTooltip title="Naplánovaná">
													<HourglassTopIcon color="warning" sx={{ cursor: "help" }} />
												</BlTooltip>
											)}
											{data.state === InfoWarningState.Active && (
												<BlTooltip title="Aktivní">
													<PlayArrowIcon color="success" sx={{ cursor: "help" }} />
												</BlTooltip>
											)}
										</Stack>
										<Typography>{data.text}</Typography>
									</Stack>
								</StyledCell>
							);
						case "validFrom":
							return (
								<StyledCell
									color={
										data.state === InfoWarningState.Expired
											? "#e1e1e1"
											: "textPrimary"
									}
								>
									{!!data.validFrom ? formatDate(new Date(data.validFrom)) : ""}
								</StyledCell>
							);
						case "validTo":
							return (
								<StyledCell
									color={
										data.state === InfoWarningState.Expired
											? "#e1e1e1"
											: "textPrimary"
									}
								>
									{!!data.validTo ? formatDate(new Date(data.validTo)) : ""}
								</StyledCell>
							);
						case "url":
							return (
								<StyledCell
									color={
										data.state === InfoWarningState.Expired
											? "#e1e1e1"
											: "textPrimary"
									}
								>
									{data.url}
								</StyledCell>
							);
						case "type":
							return (
								<StyledCell
									color={
										data.state === InfoWarningState.Expired
											? "#e1e1e1"
											: "textPrimary"
									}
								>
									{getInfoWarningTypeDisplayName(data.type)}
								</StyledCell>
							);
						case "visibility":
							return (
								<StyledCell
									color={
										data.state === InfoWarningState.Expired
											? "#e1e1e1"
											: "textPrimary"
									}
								>
									{getInfoWarningVisibilityDisplayName(data.visibility)}
								</StyledCell>
							);
						case "placement":
							return (
								<StyledCell
									color={
										data.state === InfoWarningState.Expired
											? "#e1e1e1"
											: "textPrimary"
									}
								>
									{getInfoWarningPlacementsDisplayName(data.placement)}
								</StyledCell>
							);
						case "productTypeID":
							return (
								<StyledCell
									color={
										data.state === InfoWarningState.Expired
											? "#e1e1e1"
											: "textPrimary"
									}
								>
									{!!data.productTypeID
										? getProductTypesDisplayName(data.productTypeID)
										: ""}
								</StyledCell>
							);
						case "description":
							return (
								<StyledCell
									color={
										data.state === InfoWarningState.Expired
											? "#e1e1e1"
											: "textPrimary"
									}
								>
									{renderDescription(
										data.description,
										data.state === InfoWarningState.Expired
											? "#e1e1e1"
											: "inherit"
									)}
								</StyledCell>
							);
						case "action":
							return (
								<StyledCell
									color={
										data.state === InfoWarningState.Expired
											? "#e1e1e1"
											: "textPrimary"
									}
								>
									<BlConfirmButton
										modalTitle={"Smazání"}
										modalContent={
											"Smazat infolištu bez odeslání notifikačního e-mailu?"
										}
										onResult={(result) => {
											if (!!result) {
												deleteInfoWarning(data.infoWarningID);
											}
										}}
									>
										<DeleteIcon />
									</BlConfirmButton>
								</StyledCell>
							);
						default:
							return (
								<StyledCell
									color={
										data.state === InfoWarningState.Expired
											? "#e1e1e1"
											: "textPrimary"
									}
								>
									{data[id]}
								</StyledCell>
							);
					}
				}}
			/>
		</>
	);
};

export default InfoWarningsTable;
